import { useState } from 'react'
import { ChevronDown, ChevronUp } from 'react-bootstrap-icons'

import styles from './styles.module.scss'

export type DescBlockId = 'vendor' | 'part' | 'car' | 'warehouse' | 'global'

export const descBlockIds: DescBlockId[] = ['vendor', 'part', 'car', 'warehouse', 'global']

const blockTitle: Record<DescBlockId | 'prefix' | 'suffix', string> = {
    prefix: 'Текст в начало',
    suffix: 'Текст в конец',
    vendor: 'Артикул',
    part: 'Описание запчасти',
    car: 'Доп. описание автомобиля',
    warehouse: 'Доп. описание склада',
    global: 'Доп. описание для всех объявлений',
}

const orderByMarketplace: Record<string, DescBlockId[]> = {
    avito: ['vendor', 'part', 'car', 'warehouse', 'global'],
    drom: ['vendor', 'part', 'car', 'warehouse', 'global'],
    avtoto: ['vendor', 'part', 'car', 'warehouse', 'global'],
}

const standardOrder: DescBlockId[] = ['part', 'car', 'warehouse', 'global', 'vendor']

export const defaultDescOrder = (marketplace: string): DescBlockId[] => (
    (orderByMarketplace[marketplace] || standardOrder).slice()
)

export const normalizeDescOrder = (order: unknown, marketplace: string): DescBlockId[] => {
    const seen = new Set<string>()
    const result: DescBlockId[] = []
    const source = Array.isArray(order) ? order : defaultDescOrder(marketplace)
    for (const id of source) {
        if (descBlockIds.includes(id as DescBlockId) && !seen.has(id)) {
            seen.add(id)
            result.push(id as DescBlockId)
        }
    }
    for (const id of descBlockIds) {
        if (!seen.has(id)) result.push(id)
    }
    return result
}

interface IProps {
    order: DescBlockId[]
    onMove: (from: number, to: number) => void
}

const MarketplaceDescScheme = ({ order, onMove }: IProps) => {
    const [dragIndex, setDragIndex] = useState<number | null>(null)
    const [overIndex, setOverIndex] = useState<number | null>(null)

    const finishDrag = () => {
        setDragIndex(null)
        setOverIndex(null)
    }

    return (
        <figure className={styles.scheme}>
            <figcaption className={styles.caption}>Как соберётся описание</figcaption>
            <ol className={styles.blocks}>
                <li className={`${styles.block} ${styles.input}`}>{blockTitle.prefix}</li>
                {order.map((id, index) => (
                    <li
                        key={id}
                        className={[
                            styles.block,
                            styles.sortable,
                            dragIndex === index ? styles.dragging : '',
                            overIndex === index && dragIndex !== index ? styles.dropTarget : '',
                        ].filter(Boolean).join(' ')}
                        draggable
                        onDragStart={event => {
                            event.dataTransfer.effectAllowed = 'move'
                            event.dataTransfer.setData('text/plain', String(index))
                            setDragIndex(index)
                        }}
                        onDragOver={event => {
                            event.preventDefault()
                            event.dataTransfer.dropEffect = 'move'
                            if (overIndex !== index) setOverIndex(index)
                        }}
                        onDrop={event => {
                            event.preventDefault()
                            const from = Number(event.dataTransfer.getData('text/plain'))
                            if (!Number.isNaN(from)) onMove(from, index)
                            finishDrag()
                        }}
                        onDragEnd={finishDrag}
                    >
                        <span className={styles.blockTitle}>{blockTitle[id]}</span>
                        <span className={styles.moves}>
                            <button
                                type="button"
                                className={styles.move}
                                aria-label="Выше"
                                disabled={index === 0}
                                onClick={() => onMove(index, index - 1)}
                            >
                                <ChevronUp size={14} />
                            </button>
                            <button
                                type="button"
                                className={styles.move}
                                aria-label="Ниже"
                                disabled={index === order.length - 1}
                                onClick={() => onMove(index, index + 1)}
                            >
                                <ChevronDown size={14} />
                            </button>
                        </span>
                    </li>
                ))}
                <li className={`${styles.block} ${styles.input}`}>{blockTitle.suffix}</li>
            </ol>
        </figure>
    )
}

export default MarketplaceDescScheme
